import axios from 'axios';
import { apiClient } from './api-client';

/**
 * D-131 — job-level Customer Modification Requests (staff surface).
 *
 * Mirrors the shapes `ModificationRoundController` and
 * `QuotationRevisionController` return (`ROUND_SELECT`, `ENTRY_SELECT`,
 * `REVISION_SELECT` in the backend services). Nothing here is derived from
 * the legacy `inspections` columns; the round is the record.
 *
 * M5 consumes the read routes (gated server-side on `inspection:view-commercial`)
 * and the two Operations write routes (gated on `inspection:modification:review`).
 * The Commercial write routes are deliberately NOT wrapped here yet — M6 adds
 * them. Operations has no path to a customer, a quotation or a price from this
 * module, and the backend would refuse one anyway.
 */

export type ModificationRoundState =
  | 'SUBMITTED'
  | 'OPS_REVIEW'
  | 'AWAITING_CUSTOMER_CLARIFICATION'
  | 'COMMERCIAL_DECISION'
  | 'AWAITING_CUSTOMER_DECISION'
  | 'CLOSED_ACCEPTED_NO_CHANGE'
  | 'CLOSED_ACCEPTED'
  | 'CLOSED_REJECTED'
  | 'CLOSED_DECLINED'
  | 'CLOSED_NOT_FEASIBLE'
  | 'CLOSED_SUPERSEDED_BY_ROUND';

export type ModificationOwner = 'COMMERCIAL' | 'OPERATIONS';
export type ModificationWaitingFor = 'COMMERCIAL' | 'OPERATIONS' | 'CUSTOMER';
export type ModificationClassification = 'TECHNICAL' | 'COMMERCIAL' | 'MIXED' | 'UNCLEAR';
export type OpsFeasibilityVerdict =
  'FEASIBLE' | 'FEASIBLE_WITH_ALTERNATIVE' | 'NOT_FEASIBLE' | 'MORE_INFORMATION_REQUIRED';
export type OpsScopeImpact = 'NONE' | 'REDUCED' | 'INCREASED' | 'CHANGED';
/**
 * The full Prisma enum. Only the first three are CLOSURES (`POST …/close`);
 * `ACCEPTED_WITH_REVISION` is stamped by the server on T11 (send revision) and
 * `ALTERNATIVE_OFFERED` is never written by any route today — both are refused
 * by the close route (design §8.2, state machine `CLOSE`).
 */
export type CommercialOutcome =
  | 'ACCEPTED_NO_CHANGE'
  | 'DECLINED'
  | 'NOT_FEASIBLE'
  | 'ACCEPTED_WITH_REVISION'
  | 'ALTERNATIVE_OFFERED';
export type ClosureOutcome = 'ACCEPTED_NO_CHANGE' | 'DECLINED' | 'NOT_FEASIBLE';
export type ModificationEntryVisibility = 'CUSTOMER' | 'INTERNAL';
export type ModificationEntryType =
  | 'REQUESTED'
  | 'ADDED'
  | 'TRIAGED'
  | 'REFERRED_TO_OPS'
  | 'OPS_VERDICT'
  | 'CLARIFICATION_REQUESTED'
  | 'CUSTOMER_REPLIED'
  | 'INTERNAL_NOTE'
  | 'NOTE_PUBLISHED'
  | 'REVISION_SENT'
  | 'CUSTOMER_DECIDED'
  | 'CLOSED';
export type ModificationAuthorKind = 'CUSTOMER' | 'STAFF' | 'SYSTEM';
export type QuotationRevisionStatus = 'DRAFT' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'SUPERSEDED';

export interface ModificationRound {
  id: string;
  inspectionId: string;
  roundNumber: number;
  state: ModificationRoundState;
  currentOwner: ModificationOwner | null;
  waitingFor: ModificationWaitingFor | null;
  assignedUserId: string | null;
  requestText: string;
  requestedByContactId: string | null;
  recordedByUserId: string | null;
  againstRevisionId: string;
  resultingRevisionId: string | null;
  clarificationReturnState: ModificationRoundState | null;
  classification: ModificationClassification | null;
  classifiedById: string | null;
  classifiedAt: string | null;
  opsVerdict: OpsFeasibilityVerdict | null;
  opsTechnicalNote: string | null;
  opsAlternativeDescription: string | null;
  opsScopeImpact: OpsScopeImpact | null;
  opsAffectsParts: boolean;
  opsAffectsLabour: boolean;
  opsRespondedById: string | null;
  opsRespondedAt: string | null;
  commercialOutcome: CommercialOutcome | null;
  closureMessage: string | null;
  closedById: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;

  // ── Request Thread read model (Stage A) ────────────────────────────────────
  // Derived server-side from data that already exists; no column backs any of
  // them. Additive — every field above keeps its meaning. Stage A ships the
  // contract only; the surfaces that render these arrive in Stages B-D.

  /**
   * The request's own words as they stand now: the newest customer-meaning
   * entry, or `requestText` when nothing has been added. `requestText` remains
   * the archival OPENING statement and is never rewritten.
   */
  latestCustomerRequestText: string | null;
  /** The JOB's priority, read-only. `null` when the job has none. */
  priority: ThreadPriority | null;
  /** Request #N-1 when this round succeeded it; otherwise `null`. */
  previousRequestNumber: number | null;
  /** Request #N+1 when this round was superseded; otherwise `null`. */
  nextRequestNumber: number | null;
}

/** The job's priority as surfaced on a Request Thread. Never set from a thread. */
export interface ThreadPriority {
  id: string;
  name: string;
  colorHex: string;
  isEmergency: boolean;
}

export interface ModificationEntry {
  id: string;
  roundId: string;
  entryType: ModificationEntryType;
  authorKind: ModificationAuthorKind;
  actorUserId: string | null;
  actorContactId: string | null;
  visibility: ModificationEntryVisibility;
  body: string | null;
  fromOwner: ModificationOwner | null;
  toOwner: ModificationOwner | null;
  fromWaitingFor: ModificationWaitingFor | null;
  toWaitingFor: ModificationWaitingFor | null;
  createdAt: string;
}

export interface QuotationRevision {
  id: string;
  inspectionId: string;
  revisionNumber: number;
  status: QuotationRevisionStatus;
  quotationReference: string | null;
  documentAssetId: string | null;
  documentName: string | null;
  attachmentId: string | null;
  preparedById: string;
  preparedAt: string;
  sentById: string | null;
  sentAt: string | null;
  decidedAt: string | null;
  originModificationRoundId: string | null;
  revisionReason: string | null;
  supersededByRevisionId: string | null;
  createdAt: string;
}

/** Body of `POST …/ops-verdict` — `RecordOpsVerdictDto`. No price, no document. */
export interface RecordOpsVerdictPayload {
  verdict: OpsFeasibilityVerdict;
  technicalNote: string;
  alternativeDescription?: string;
  clarificationQuestion?: string;
  scopeImpact?: OpsScopeImpact;
  affectsParts?: boolean;
  affectsLabour?: boolean;
}

/** `RecordOpsVerdictDto` / `InternalNoteDto` text bounds (REQUEST_MIN/REQUEST_MAX). */
export const MODIFICATION_TEXT_MAX = 4000;

const base = (inspectionId: string) => `/inspections/${inspectionId}/modification-rounds`;

export const inspectionModificationService = {
  /** Newest round first, as the API orders them. */
  listRounds: (inspectionId: string): Promise<ModificationRound[]> =>
    apiClient
      .get<ModificationRound[]>(base(inspectionId))
      .then((r) => (Array.isArray(r.data) ? r.data : [])),

  getRound: (inspectionId: string, roundId: string): Promise<ModificationRound> =>
    apiClient.get<ModificationRound>(`${base(inspectionId)}/${roundId}`).then((r) => r.data),

  /** The staff timeline — INTERNAL entries included, oldest first. */
  listEntries: (inspectionId: string, roundId: string): Promise<ModificationEntry[]> =>
    apiClient
      .get<ModificationEntry[]>(`${base(inspectionId)}/${roundId}/entries`)
      .then((r) => (Array.isArray(r.data) ? r.data : [])),

  listRevisions: (inspectionId: string): Promise<QuotationRevision[]> =>
    apiClient
      .get<QuotationRevision[]>(`/inspections/${inspectionId}/quotation-revisions`)
      .then((r) => (Array.isArray(r.data) ? r.data : [])),

  /** T5 / T6 — the ONE Operations workflow write. */
  recordOpsVerdict: (
    inspectionId: string,
    roundId: string,
    payload: RecordOpsVerdictPayload,
  ): Promise<ModificationRound> =>
    apiClient
      .post<ModificationRound>(`${base(inspectionId)}/${roundId}/ops-verdict`, payload)
      .then((r) => r.data),

  /** T18 — an internal Operations note; never customer-visible. */
  addOpsNote: (inspectionId: string, roundId: string, body: string): Promise<ModificationRound> =>
    apiClient
      .post<ModificationRound>(`${base(inspectionId)}/${roundId}/ops-note`, { body })
      .then((r) => r.data),

  // ── Commercial (D-131 M6) — `inspection:modification:manage` server-side ──

  /** T2 / T3 / T4 — classify and route. `question` is required for UNCLEAR. */
  triage: (
    inspectionId: string,
    roundId: string,
    payload: TriageRoundPayload,
  ): Promise<ModificationRound> =>
    apiClient
      .post<ModificationRound>(`${base(inspectionId)}/${roundId}/triage`, payload)
      .then((r) => r.data),

  /** T13 — send the round back to Operations for another feasibility pass. */
  referToOps: (
    inspectionId: string,
    roundId: string,
    payload: { note?: string },
  ): Promise<ModificationRound> =>
    apiClient
      .post<ModificationRound>(`${base(inspectionId)}/${roundId}/refer-to-ops`, payload)
      .then((r) => r.data),

  /** T12 — ask the customer a question from COMMERCIAL_DECISION. */
  clarify: (
    inspectionId: string,
    roundId: string,
    payload: { question: string },
  ): Promise<ModificationRound> =>
    apiClient
      .post<ModificationRound>(`${base(inspectionId)}/${roundId}/clarify`, payload)
      .then((r) => r.data),

  /** Appends a customer-visible copy of the Operations technical note. */
  publishOpsNote: (inspectionId: string, roundId: string): Promise<ModificationRound> =>
    apiClient
      .post<ModificationRound>(`${base(inspectionId)}/${roundId}/publish-ops-note`)
      .then((r) => r.data),

  /** T18 — an internal Commercial note; never customer-visible. */
  addCommercialNote: (
    inspectionId: string,
    roundId: string,
    body: string,
  ): Promise<ModificationRound> =>
    apiClient
      .post<ModificationRound>(`${base(inspectionId)}/${roundId}/note`, { body })
      .then((r) => r.data),

  /** T8 / T9 / T10 — close with a customer-readable outcome and message. */
  close: (
    inspectionId: string,
    roundId: string,
    payload: CloseRoundPayload,
  ): Promise<ModificationRound> =>
    apiClient
      .post<ModificationRound>(`${base(inspectionId)}/${roundId}/close`, payload)
      .then((r) => r.data),

  // ── Quotation revisions (D-131 M6) — `inspection:commercial:send` server-side ──

  /** Creates or updates the one DRAFT revision (reference / reason / origin round). */
  upsertDraftRevision: (
    inspectionId: string,
    payload: UpsertDraftRevisionPayload,
  ): Promise<QuotationRevision> =>
    apiClient
      .post<QuotationRevision>(`/inspections/${inspectionId}/quotation-revisions`, payload)
      .then((r) => r.data),

  /** T11 — sends the DRAFT and moves the round to AWAITING_CUSTOMER_DECISION. */
  sendRevision: (
    inspectionId: string,
    revisionId: string,
    payload: { roundId: string },
  ): Promise<{ round: ModificationRound; revision: QuotationRevision }> =>
    apiClient
      .post<{ round: ModificationRound; revision: QuotationRevision }>(
        `/inspections/${inspectionId}/quotation-revisions/${revisionId}/send`,
        payload,
      )
      .then((r) => r.data),

  // ── Revision document (D-131 G-4) — `inspection:commercial:send` / download ──

  /** Attaches (or replaces) the ERP quotation PDF on a DRAFT revision. Multipart; PDF only. */
  attachRevisionDocument: (
    inspectionId: string,
    revisionId: string,
    file: File,
  ): Promise<QuotationRevision> => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient
      .post<QuotationRevision>(
        `/inspections/${inspectionId}/quotation-revisions/${revisionId}/document`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      .then((r) => r.data);
  },

  /** Removes the PDF from a DRAFT revision. */
  removeRevisionDocument: (inspectionId: string, revisionId: string): Promise<QuotationRevision> =>
    apiClient
      .delete<QuotationRevision>(
        `/inspections/${inspectionId}/quotation-revisions/${revisionId}/document`,
      )
      .then((r) => r.data),

  /** AME-signed download URL for any revision's PDF (superseded ones are retained, OQ-2). */
  getRevisionDocumentUrl: (inspectionId: string, revisionId: string): Promise<string | null> =>
    apiClient
      .get<{ url: string | null }>(
        `/inspections/${inspectionId}/quotation-revisions/${revisionId}/document`,
      )
      .then((r) => r.data?.url ?? null),
};

/** Body of `POST …/triage` — `TriageModificationRoundDto`. */
export interface TriageRoundPayload {
  classification: ModificationClassification;
  /** Required iff `classification === 'UNCLEAR'` (T4). */
  question?: string;
}

/** Body of `POST …/close` — `CloseModificationRoundDto`. Only closures are accepted. */
export interface CloseRoundPayload {
  outcome: ClosureOutcome;
  closureMessage: string;
}

/** Body of `POST /inspections/:id/quotation-revisions` — `CreateQuotationRevisionDto`. */
export interface UpsertDraftRevisionPayload {
  quotationReference?: string;
  revisionReason?: string;
  roundId?: string;
}

/** The HTTP status of an API failure, or `null` when it was not an HTTP reply. */
export function apiErrorStatus(error: unknown): number | null {
  return axios.isAxiosError(error) ? (error.response?.status ?? null) : null;
}
